import React, { useState } from 'react';
import { Box, Grid, TextField } from '@material-ui/core';
import { CustomDialog, NumberFormatCustom } from '../../../custom-forms';
import { TextValidator } from 'react-material-ui-form-validator';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { CaseCalculationAlternativePart } from '../../../interfaces/Case';
import { IReferential } from '../../../interfaces/IReferential';
import { Organization } from '../../../interfaces/Organization';
import { emptyAlternativeDraft } from '../data/factories';
import { useTranslate } from '../../../hooks/useTranslate';

export interface CalculationAlternativePartAddDialogProps {
  open: boolean;
  qualities: IReferential[];
  suppliers: Organization[];
  onClose: () => void;
  onSave: (part: CaseCalculationAlternativePart) => void;
}

const CalculationAlternativePartAddDialog: React.FC<CalculationAlternativePartAddDialogProps> = ({
  open,
  qualities,
  suppliers,
  onClose,
  onSave
}) => {
  const t = useTranslate();
  const [alternativePart, setAlternativePart] =
    useState<CaseCalculationAlternativePart>(emptyAlternativeDraft);

  const handleName = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setAlternativePart((prev) => ({ ...prev, name }));
  };

  const handleGuideNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const alternativeGuideNumber = e.target.value;
    setAlternativePart((prev) => ({ ...prev, alternativeGuideNumber }));
  };

  const handlePriceChange = (e: any) => {
    const value = e.target.value;
    setAlternativePart((prev) => ({
      ...prev,
      alternativePrice: value === '' ? 0 : parseFloat(value)
    }));
  };

  const handleQuality = (_e: React.SyntheticEvent, v: IReferential | null) =>
    setAlternativePart((prev) => ({ ...prev, alternativePartQuality: v }));

  const handleSupplier = (_e: React.SyntheticEvent, v: Organization | null) =>
    setAlternativePart((prev) => ({ ...prev, supplier: v }));

  const handleDeliveryTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    const deliveryTime = e.target.value;
    setAlternativePart((prev) => ({ ...prev, deliveryTime }));
  };

  const handleProducer = (e: React.ChangeEvent<HTMLInputElement>) => {
    const producer = e.target.value;
    setAlternativePart((prev) => ({ ...prev, producer }));
  };

  const handleStockLocation = (e: React.ChangeEvent<HTMLInputElement>) => {
    const stockLocation = e.target.value;
    setAlternativePart((prev) => ({ ...prev, stockLocation }));
  };

  const handleAvailableQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAlternativePart((prev) => ({
      ...prev,
      availableQuantity: e.target.value === null ? 0 : parseFloat(e.target.value)
    }));
  };

  const handleRemarks = (e: React.ChangeEvent<HTMLInputElement>) => {
    const remarks = e.target.value;
    setAlternativePart((prev) => ({ ...prev, remarks }));
  };

  const submit = () => {
    onSave(alternativePart);
    onClose();
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      onSubmit={submit}
      title={t(
        'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_ADD_ALTERNATIVE_PART_LABEL',
        'Adaugă piesa alternativă'
      )}
      cancelLabel={t('ALT_CALC_CANCEL', 'Renunță')}
      confirmLabel={t('SAVE', 'Salveaza')}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box mb={2}>
            <TextValidator
              variant="outlined"
              fullWidth
              id="name"
              name="name"
              label={t('CALCULATION_DETAILS_DESCRIPTION_HEADER', 'Descriere')}
              value={alternativePart.name}
              onChange={handleName}
              validators={['required']}
              errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
            />
          </Box>
          <Box mb={2}>
            <TextValidator
              variant="outlined"
              fullWidth
              id="alternativeGuideNumber"
              name="alternativeGuideNumber"
              label={t('CALCULATION_DETAILS_GUIDE_NUMBER_HEADER', 'Cod')}
              value={alternativePart.alternativeGuideNumber}
              onChange={handleGuideNumber}
              validators={['required']}
              errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
            />
          </Box>
          <Box mb={2}>
            <TextValidator
              variant="outlined"
              fullWidth
              id="alternativePrice"
              name="alternativePrice"
              label={t('CALCULATION_DETAILS_UNIT_PRICE_HEADER', 'Pret Unitar')}
              value={alternativePart.alternativePrice || ''}
              onChange={handlePriceChange}
              InputProps={{ inputComponent: NumberFormatCustom }}
              validators={['required', 'minNumber:1']}
              errorMessages={[
                t('VALIDATORS_REQUIRED', 'Campul este obligatoriu'),
                t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')
              ]}
            />
          </Box>
          <Box mb={2}>
            <Autocomplete
              fullWidth
              options={qualities}
              value={alternativePart.alternativePartQuality ?? null}
              getOptionLabel={(o: IReferential) => o.code ?? ''}
              onChange={handleQuality}
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  variant="outlined"
                  fullWidth
                  name="quality"
                  value={alternativePart.alternativePartQuality}
                  label={t('CALCULATION_DETAILS_QUALITY_HEADER', 'Calitate')}
                  validators={['required']}
                  errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
                />
              )}
            />
          </Box>
          <Box mb={2}>
            <Autocomplete
              fullWidth
              options={suppliers}
              value={alternativePart.supplier ?? null}
              getOptionLabel={(o: Organization) => o.name}
              onChange={handleSupplier}
              renderInput={(params) => (
                <TextValidator
                  {...params}
                  variant="outlined"
                  fullWidth
                  name="supplier"
                  value={alternativePart.supplier}
                  label={t('CALCULATION_DETAILS_SUPPLIER_HEADER', 'Furnizor')}
                  validators={['required']}
                  errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
                />
              )}
            />
          </Box>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Box mb={2}>
            <TextValidator
              variant="outlined"
              fullWidth
              id="deliveryTime"
              name="deliveryTime"
              label={t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_DELIVERY_LABEL', 'Livrare')}
              value={alternativePart.deliveryTime}
              onChange={handleDeliveryTime}
              validators={['required']}
              errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
            />
          </Box>
          <Box mb={2}>
            <TextValidator
              variant="outlined"
              fullWidth
              id="producer"
              name="producer"
              label={t('CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_OWNER_LABEL', 'Producator')}
              value={alternativePart.producer}
              onChange={handleProducer}
              validators={['required']}
              errorMessages={[t('VALIDATORS_REQUIRED', 'Campul este obligatoriu')]}
            />
          </Box>
          <Box mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              id="stockLocation"
              label={t(
                'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_STOCK_LOCATION_LABEL',
                'Locatie'
              )}
              value={alternativePart.stockLocation}
              onChange={handleStockLocation}
            />
          </Box>
          <Box mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              id="availableQuantity"
              InputProps={{ inputComponent: NumberFormatCustom }}
              label={t(
                'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_QUANTITY_LABEL',
                'Cantitate'
              )}
              value={alternativePart.availableQuantity}
              onChange={handleAvailableQuantityChange}
            />
          </Box>
          <Box mb={2}>
            <TextField
              variant="outlined"
              fullWidth
              multiline
              rows={2}
              id="remarks"
              label={t(
                'CASE_CALCULATION_ALTERNATIVE_CALCULATION_DETAILS_REMARKS_LABEL',
                'Observatii'
              )}
              value={alternativePart.remarks}
              onChange={handleRemarks}
            />
          </Box>
        </Grid>
      </Grid>
    </CustomDialog>
  );
};

export default CalculationAlternativePartAddDialog;
